import {
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
  type KeyboardEvent,
} from "react";
import { AnimatePresence, motion } from "framer-motion";
import { MapPin, Search } from "lucide-react";

import { useAutoFocus } from "../../hooks/useAutoFocus";
import type {
  TargetLocation,
  TargetLocationSuggestion,
} from "../../lib/target-location";
import { getTargetLocationProvider } from "../../lib/target-location-provider";
import { cn } from "../../lib/utils";

interface AreaSearchProps {
  autoFocus?: boolean;
  /**
   * Fires when the buyer is done with the field: a click on a suggestion, Enter,
   * or blur. `picked` is set when the committed value IS a suggestion: a click,
   * or Enter/blur on text that is the start of the top suggestion's main text
   * (that suggestion is committed in place of the typed text). Otherwise the raw
   * text is committed with no `picked`, and the server decides whether it
   * resolves. Typing alone never calls this.
   */
  onCommit?: (value: string, picked?: TargetLocation) => void;
  onChange: (value: string, picked?: TargetLocation) => void;
  value: string | null;
  /**
   * Size + padding utilities for the input, so callers can align both the
   * type scale AND the box height with their own surface. Default
   * `px-3 py-4 text-lg` keeps the hero onboarding + Passport-row callers
   * byte-identical; the M3 funnel passes a smaller size + tighter padding so
   * the box matches its button text. Owns the size/padding utilities OUTRIGHT
   * (not appended) because `cn` is a plain join — a second `text-*`/`py-*`
   * wouldn't reliably win the cascade.
   */
  inputClassName?: string;
  /**
   * The box around the input. Default is the legacy wizard / Passport-row
   * shell (12px, 2px border); the funnel passes its own flat 48px hairline
   * shell so this search reads as the same control as every other funnel
   * input. Focus color/ring stay motion-driven below either way.
   */
  shellClassName?: string;
}

export function AreaSearch({
  autoFocus = false,
  onChange,
  onCommit,
  value,
  inputClassName = "px-3 py-4 text-lg",
  shellClassName = "flex items-center rounded-xl border-2 bg-card",
}: AreaSearchProps) {
  const [query, setQuery] = useState(value || "");
  const [isFocused, setIsFocused] = useState(false);
  // Escape closes the list without leaving the field; typing or an arrow key
  // reopens it.
  const [dismissed, setDismissed] = useState(false);
  // The option the keyboard has moved to (aria-activedescendant), -1 = none.
  // Focus itself never leaves the input.
  const [activeIndex, setActiveIndex] = useState(-1);
  const listboxId = useId();
  const provider = useMemo(() => getTargetLocationProvider(), []);
  const [lookupState, setLookupState] = useState<{
    query: string;
    suggestions: TargetLocationSuggestion[];
  }>({
    query: "",
    suggestions: [],
  });
  const inputRef = useRef<HTMLInputElement>(null);

  useAutoFocus(inputRef, autoFocus);

  const googleSuggestions =
    provider && query.trim().length >= 3 && lookupState.query === query.trim()
      ? lookupState.suggestions
      : [];
  const suggestions = googleSuggestions.slice(0, 5);
  const isExpanded = isFocused && !dismissed && suggestions.length > 0;
  const activeOption =
    isExpanded && activeIndex < suggestions.length ? activeIndex : -1;
  const optionId = (index: number) => `${listboxId}-option-${index}`;
  // Whether a lookup for exactly this text has answered, so the live region
  // can say "no matching places" instead of staying silent.
  const lookupSettled =
    !!provider &&
    query.trim().length >= 3 &&
    lookupState.query === query.trim();
  const statusMessage = !isFocused || !lookupSettled
    ? ""
    : suggestions.length === 0
      ? "No matching places. Press Enter to use what you typed."
      : `${suggestions.length} ${suggestions.length === 1 ? "suggestion" : "suggestions"} available. Use the up and down arrows to choose.`;
  const blurTimeoutRef = useRef<number | null>(null);
  const dropdownRef = useRef<HTMLUListElement>(null);
  // The value last handed to onCommit, seeded with the value the field opens
  // on. Enter then blur (or a click followed by the input losing focus) must
  // not commit the same text twice - the second, raw commit would drop the
  // place_id the first one carried - and focusing then leaving the field
  // without editing it must not "commit" its existing value. Typing clears it.
  const committedRef = useRef<string | null>(value || null);

  useEffect(() => {
    const trimmed = query.trim();
    if (!provider || trimmed.length < 3) {
      return;
    }

    let cancelled = false;

    void provider
      .search(trimmed)
      .then((nextSuggestions) => {
        if (cancelled) {
          return;
        }
        setLookupState({
          query: trimmed,
          suggestions: nextSuggestions,
        });
      })
      .catch(() => {
        if (cancelled) {
          return;
        }
        setLookupState({
          query: trimmed,
          suggestions: [],
        });
      });

    return () => {
      cancelled = true;
    };
  }, [provider, query]);
  useEffect(() => {
    return () => {
      if (blurTimeoutRef.current !== null) {
        window.clearTimeout(blurTimeoutRef.current);
      }
    };
  }, []);

  const handleSelect = (suggestion: TargetLocationSuggestion) => {
    // `mainText` is the picker's own matching aid; the committed value is the
    // plain TargetLocation.
    const area: TargetLocation = {
      label: suggestion.label,
      kind: suggestion.kind,
      ...(suggestion.place_id ? { place_id: suggestion.place_id } : {}),
    };
    committedRef.current = area.label;
    setQuery(area.label);
    setActiveIndex(-1);
    onChange(area.label, area);
    onCommit?.(area.label, area);
    setIsFocused(false);
  };

  // Enter and blur. An option the keyboard moved to is committed as picked.
  // Otherwise commits the top suggestion when the typed text is a
  // case-insensitive prefix of its main text ("ballard" -> "Ballard, Seattle,
  // WA"); anything else is committed verbatim, with no place_id.
  const commitTypedValue = (raw: string) => {
    if (activeOption >= 0) {
      handleSelect(suggestions[activeOption]);
      return;
    }
    if (committedRef.current === raw) {
      return;
    }
    const top = topSuggestionFor(raw, suggestions);
    if (top) {
      handleSelect(top);
      return;
    }
    committedRef.current = raw;
    onCommit?.(raw);
  };

  // WAI-ARIA 1.2 combobox keys. Arrow keys open a dismissed list and move
  // through it, wrapping at both ends. Home/End move through the list only
  // once an option is active; before that they stay with the text caret.
  const handleListKey = (event: KeyboardEvent<HTMLInputElement>) => {
    const count = suggestions.length;
    switch (event.key) {
      case "ArrowDown":
      case "ArrowUp": {
        if (count === 0) {
          return;
        }
        event.preventDefault();
        setDismissed(false);
        setIsFocused(true);
        if (event.altKey && !isExpanded) {
          return;
        }
        const down = event.key === "ArrowDown";
        setActiveIndex(
          activeOption < 0
            ? down
              ? 0
              : count - 1
            : (activeOption + (down ? 1 : -1) + count) % count,
        );
        return;
      }
      case "Home":
      case "End":
        if (activeOption < 0) {
          return;
        }
        event.preventDefault();
        setActiveIndex(event.key === "Home" ? 0 : count - 1);
        return;
      case "Escape":
        if (!isExpanded) {
          return;
        }
        // Only the list closes; an enclosing editor must not also see this
        // Escape as "cancel".
        event.preventDefault();
        event.stopPropagation();
        setDismissed(true);
        setActiveIndex(-1);
        return;
    }
  };

  return (
    <div className="relative">
      <motion.div
        animate={{
          borderColor: isFocused ? "var(--primary)" : "var(--border)",
          boxShadow: isFocused ? "0 0 0 3px rgba(15, 110, 86, 0.1)" : "none",
        }}
        transition={{ duration: 0.2 }}
        className={shellClassName}
      >
        <Search aria-hidden="true" className="ml-3.5 h-[18px] w-[18px] flex-shrink-0 text-muted-foreground/70" />
        <input
          ref={inputRef}
          type="text"
          value={query}
          role="combobox"
          aria-label="Target city or ZIP"
          aria-autocomplete="list"
          aria-expanded={isExpanded}
          aria-controls={listboxId}
          aria-activedescendant={
            activeOption >= 0 ? optionId(activeOption) : undefined
          }
          autoComplete="off"
          onChange={(event) => {
            const nextQuery = event.target.value;
            committedRef.current = null;
            setQuery(nextQuery);
            setActiveIndex(-1);
            setDismissed(false);
            // A pick leaves the input focused (see the dropdown's mousedown
            // guard), so typing again has to reopen the list itself.
            setIsFocused(true);
            onChange(nextQuery);
          }}
          onFocus={() => setIsFocused(true)}
          onKeyDown={(event) => {
            if (event.key === "Enter" && !event.nativeEvent.isComposing) {
              event.preventDefault();
              commitTypedValue(event.currentTarget.value);
              return;
            }
            handleListKey(event);
          }}
          onBlur={(event) => {
            // Focus moving onto a suggestion is a click in progress: the click
            // commits that suggestion, so committing the raw text first would
            // race it (and, on the Passport row, start a save without its
            // place_id).
            const next = event.relatedTarget;
            const movedOntoSuggestion =
              next instanceof Node && dropdownRef.current?.contains(next);
            if (!movedOntoSuggestion) {
              commitTypedValue(event.currentTarget.value);
            }
            if (blurTimeoutRef.current !== null) {
              window.clearTimeout(blurTimeoutRef.current);
            }
            blurTimeoutRef.current = window.setTimeout(() => {
              blurTimeoutRef.current = null;
              setIsFocused(false);
              setActiveIndex(-1);
              setDismissed(false);
            }, 200);
          }}
          placeholder="Enter city or ZIP code"
          className={cn(
            "flex-1 bg-transparent text-foreground outline-none",
            inputClassName,
            "placeholder:text-muted-foreground/50"
          )}
        />
      </motion.div>

      {/* Announces the result count; kept mounted so updates are read. */}
      <p aria-live="polite" className="sr-only" role="status">
        {statusMessage}
      </p>

      <AnimatePresence>
        {isExpanded ? (
          <motion.ul
            ref={dropdownRef}
            id={listboxId}
            role="listbox"
            aria-label="Suggested places"
            // Keep focus in the input while a suggestion is pressed, so the
            // press never blurs (and so never commits) the typed text.
            onMouseDown={(event) => event.preventDefault()}
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            transition={{ duration: 0.2 }}
            className="absolute left-0 right-0 top-full z-50 mt-2 overflow-hidden rounded-xl border border-border bg-card shadow-lg"
          >
            {suggestions.map((area, index) => (
              // Options are reached through the input's
              // aria-activedescendant, never by focus: Tab from the input
              // leaves the field, which blurs and commits.
              <li
                id={optionId(index)}
                role="option"
                aria-selected={index === activeOption}
                key={area.place_id ?? `${area.kind}-${area.label}`}
                onClick={() => handleSelect(area)}
                className={cn(
                  "flex w-full cursor-pointer items-center gap-3 px-4 py-3 text-left transition-colors hover:bg-accent",
                  index === activeOption && "bg-accent"
                )}
              >
                <MapPin
                  aria-hidden="true"
                  className="h-4 w-4 flex-shrink-0 text-muted-foreground"
                />
                <div className="flex-1">
                  <p className="font-medium text-foreground">{area.label}</p>
                </div>
              </li>
            ))}
          </motion.ul>
        ) : null}
      </AnimatePresence>
    </div>
  );
}

/**
 * The first suggestion, when `typed` is a case-insensitive prefix of Google's
 * main text for it ("Ballard", or "98107" for a postal code). Matching the main
 * text rather than the whole label is what keeps "tacoma wa" from committing
 * "Tacoma, WA": the buyer typed more than the city name, so they get exactly
 * what they typed.
 */
function topSuggestionFor(
  typed: string,
  suggestions: TargetLocationSuggestion[],
): TargetLocationSuggestion | null {
  const top = suggestions[0];
  const needle = typed.trim().toLowerCase();
  if (!top?.place_id || !needle) {
    return null;
  }
  return top.mainText.toLowerCase().startsWith(needle) ? top : null;
}
